package ru.cleardocs.lkweb

import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color
import com.varabyte.kobweb.silk.theme.colors.palette.toPalette
import org.jetbrains.compose.web.css.cssRem

/**
 * Design tokens for spacing and radius. Use for consistency across components.
 */
object SiteTokens {
    object Spacing {
        val xs = 0.25.cssRem
        val sm = 0.5.cssRem
        val md = 0.75.cssRem
        val lg = 1.cssRem
        val xl = 1.25.cssRem
        val xxl = 2.cssRem
        val page = 3.cssRem
    }

    object Radius {
        val sm = 0.6.cssRem
        val md = 0.75.cssRem
        val lg = 1.cssRem
        val card = 1.25.cssRem
    }
}

class SitePalette(
    val nearBackground: Color,
    val cobweb: Color,
    val brand: Brand,
) {
    class Brand(val primary: Color, val accent: Color)
}

object SitePalettes {
    val light = SitePalette(
        nearBackground = Color.rgb(0xF8FAFC),
        cobweb = Color.rgb(0xE2E8F0),
        // Primary brand blue (slightly more "sky/голубой" than before)
        brand = SitePalette.Brand(Color.rgb(0x3B82F6), Color.rgb(0x60A5FA)),
    )
    val dark = SitePalette(
        nearBackground = Color.rgb(0x13171F),
        cobweb = Colors.LightGray.inverted(),
        // Keep primary consistent between light/dark for brand recognition
        brand = SitePalette.Brand(Color.rgb(0x3B82F6), Color.rgb(0xF3DB5B)),
    )
}

fun ColorMode.toSitePalette(): SitePalette = when (this) {
    ColorMode.LIGHT -> SitePalettes.light
    ColorMode.DARK -> SitePalettes.dark
}

/**
 * Фон для карточек/кнопок: белый в светлой теме, nearBackground в тёмной.
 */
fun ColorMode.cardItemBackground(palette: SitePalette) = when (this) {
    ColorMode.LIGHT -> Colors.White
    ColorMode.DARK -> palette.nearBackground
}

/**
 * Цвета для полей ввода (фон, текст, бордер) с учётом текущей темы.
 */
data class InputColors(val background: String, val foreground: String, val border: String)

@Composable
fun rememberInputColors(): InputColors {
    val palette = ColorMode.current.toSitePalette()
    val colorPalette = ColorMode.current.toPalette()
    return remember(palette, colorPalette) {
        InputColors(
            background = colorPalette.background.toString(),
            foreground = colorPalette.color.toString(),
            border = palette.cobweb.toString()
        )
    }
}

@InitSilk
fun initTheme(ctx: InitSilkContext) {
    with(ctx.theme.palettes) {
        light.apply {
            background = Color.rgb(0xFFFFFF)
            color = Color.rgb(0x0F172A)
        }
        dark.apply {
            background = Color.rgb(0x0B1120)
            color = Colors.White
        }
    }
}
